import { useAuth } from '../contexts/AuthContext';

// Monthly caps are abuse backstops only — mirrors backend checkUsageLimits.ts.
// They must never bind before the daily limits do for a legitimate user.
export const AI_USAGE_LIMITS = {
  free: {
    aiFoodQueries: 1000,
    aiAnalyses: 10,
    aiAssistantMessages: 200,
  },
};

export const DAILY_FREE_LIMITS = {
  aiFoodLookups: 40, // mirrors backend/src/utils/dailyLimits.ts — a ceiling, not a product limit
  aiAnalyses: 1,
  aiAssistantMessages: 5,
};

export const STREAK_BONUSES = [
  { days: 3, bonus: 2, label: '3-Day Streak' },
  { days: 7, bonus: 5, label: '7-Day Streak' },
  { days: 14, bonus: 10, label: '14-Day Streak' },
  { days: 30, bonus: 20, label: '30-Day Streak' },
];

export const useTier = () => {
  const { user } = useAuth();

  if (!user) {
    return {
      isPro: false,
      isFreeProDay: false,
      freeProDayExpiresAt: undefined as Date | string | undefined,
      tier: 'free' as const,
      usage: { aiFoodQueries: 0, aiAnalyses: 0, aiAssistantMessages: 0 },
      limits: AI_USAGE_LIMITS.free,
      dailyUsage: { aiFoodQueries: 0, aiAnalyses: 0, aiAssistantMessages: 0 },
      dailyLimits: { aiFoodLookups: DAILY_FREE_LIMITS.aiFoodLookups, aiAnalyses: DAILY_FREE_LIMITS.aiAnalyses, aiAssistantMessages: DAILY_FREE_LIMITS.aiAssistantMessages },
      streakBonus: 0,
      currentStreak: 0,
      canUseFoodLookup: false,
      canUseAnalysis: false,
      canUseAIAssistant: false,
    };
  }

  const { tier = 'free', usage } = user;
  // Free Pro Day: full Pro feature access while inside the 24h window, even
  // though tier stays 'free' (so billing/upgrade UI still treats them as free).
  const isFreeProDay =
    tier !== 'pro' &&
    !!user.freeProDayExpiresAt &&
    new Date(user.freeProDayExpiresAt).getTime() > Date.now();
  const isPro = tier === 'pro' || isFreeProDay;

  const aiFoodQueries = usage?.aiFoodQueries ?? 0;
  const aiAnalyses = usage?.aiAnalyses ?? 0;
  const aiAssistantMessages = usage?.aiAssistantMessages ?? 0;

  const dailyUsage = {
    aiFoodQueries: user.dailyUsage?.aiFoodQueries ?? 0,
    aiAnalyses: user.dailyUsage?.aiAnalyses ?? 0,
    aiAssistantMessages: user.dailyUsage?.aiAssistantMessages ?? 0,
  };

  const streakBonus = user.streakRewards?.extraAiQueries ?? 0;
  const currentStreak = user.streakRewards?.lastRewardStreak ?? 0;

  const dailyLimits = {
    aiFoodLookups: DAILY_FREE_LIMITS.aiFoodLookups + (isPro ? 0 : streakBonus),
    aiAnalyses: DAILY_FREE_LIMITS.aiAnalyses,
    aiAssistantMessages: DAILY_FREE_LIMITS.aiAssistantMessages,
  };

  // Pro users have unlimited, free users check both daily and monthly
  const canUseFoodLookup = isPro || (
    dailyUsage.aiFoodQueries < dailyLimits.aiFoodLookups &&
    aiFoodQueries < AI_USAGE_LIMITS.free.aiFoodQueries
  );
  const canUseAnalysis = isPro || (
    dailyUsage.aiAnalyses < dailyLimits.aiAnalyses &&
    aiAnalyses < AI_USAGE_LIMITS.free.aiAnalyses
  );
  const canUseAIAssistant = isPro || (
    dailyUsage.aiAssistantMessages < dailyLimits.aiAssistantMessages &&
    aiAssistantMessages < AI_USAGE_LIMITS.free.aiAssistantMessages
  );

  return {
    isPro,
    isFreeProDay,
    freeProDayExpiresAt: user.freeProDayExpiresAt,
    tier,
    usage: { aiFoodQueries, aiAnalyses, aiAssistantMessages },
    limits: AI_USAGE_LIMITS.free,
    dailyUsage,
    dailyLimits,
    streakBonus,
    currentStreak,
    canUseFoodLookup,
    canUseAnalysis,
    canUseAIAssistant,
  };
};
